import { z } from "zod";

import type { Subscription } from "@/features/billing/models";
import {
  parsePaginatedResponse,
  parseResponseData,
  privateAPI,
} from "@/shared/lib";
import type { Appointment, ScheduleAppointment } from "../models";
import {
  APPOINTMENT_TYPE,
  MEDICATION_REQUEST_APPOINTMENT_TYPES,
} from "../models";
import { appointmentSchema, providerSchema } from "../models/schemas";

export const getMedicationRequestPastAppointments = async (offSet?: number) => {
  const response = await privateAPI.get("/appointments", {
    params: {
      offSet,
      upcomingAppointments: false,
      appointmentTypes: MEDICATION_REQUEST_APPOINTMENT_TYPES,
    },
  });

  return parsePaginatedResponse(response, z.array(appointmentSchema));
};

export const getJourneySupportPastAppointments = async () => {
  const response = await privateAPI.get("/appointments", {
    params: {
      upcomingAppointments: false,
      appointmentTypes: [APPOINTMENT_TYPE.JOURNEY_SUPPORT],
    },
  });

  return parseResponseData(response, z.array(appointmentSchema));
};

export const getAllUpcomingAppointments = async () => {
  const response = await privateAPI.get("/appointments", {
    params: { upcomingAppointments: true },
  });

  return parseResponseData(response, z.array(appointmentSchema));
};

export const getMedicationRequestUpcomingAppointments = async () => {
  const response = await privateAPI.get("/appointments", {
    params: {
      upcomingAppointments: true,
      appointmentTypes: MEDICATION_REQUEST_APPOINTMENT_TYPES,
    },
  });

  return parseResponseData(response, z.array(appointmentSchema));
};

export const getJourneySupportUpcomingAppointments = async () => {
  const response = await privateAPI.get("/appointments", {
    params: {
      upcomingAppointments: true,
      appointmentTypes: [APPOINTMENT_TYPE.JOURNEY_SUPPORT],
    },
  });

  return parseResponseData(response, z.array(appointmentSchema));
};

export const scheduleAppointment = async (
  params: Omit<ScheduleAppointment, "date">,
) => {
  const { timeSlot, language, type, subscriptionId } = params;
  const response = await privateAPI.post("/appointments", {
    language,
    providerId: timeSlot.providerId,
    appointmentDate: timeSlot.dateTime,
    appointmentType: type,
    subscriptionId,
  });

  return parseResponseData(response, z.object({ provider: providerSchema }));
};

interface ReScheduleAppointmentParams {
  timeSlot: ScheduleAppointment["timeSlot"];
  id: Appointment["id"];
  subscriptionId?: Subscription["id"];
  appointmentType: ScheduleAppointment["type"];
}

export const rescheduleAppointment = async ({
  id,
  timeSlot,
  subscriptionId,
  appointmentType,
}: ReScheduleAppointmentParams) => {
  const { providerId, dateTime } = timeSlot;
  const response = await privateAPI.put(`/appointments/${id}`, {
    providerId,
    appointmentDate: dateTime,
    subscriptionId,
    appointmentType,
  });

  return parseResponseData(response, z.object({ provider: providerSchema }));
};

interface AppointmentParams {
  id: Appointment["id"];
  subscriptionId?: Appointment["subscriptionId"];
  appointmentType: ScheduleAppointment["type"];
}

export const cancelAppointment = async ({
  id,
  subscriptionId,
  appointmentType,
}: AppointmentParams) => {
  return await privateAPI.delete(`/appointments/${id}`, {
    params: { subscriptionId, appointmentType },
  });
};

export const getAppointment = async ({
  id,
  subscriptionId,
  appointmentType,
}: AppointmentParams) => {
  const response = await privateAPI.get(`/appointments/${id}`, {
    params: { subscriptionId, appointmentType },
  });

  return parseResponseData(response, appointmentSchema);
};

export const getDietitianUpcomingAppointments = async () => {
  const response = await privateAPI.get("/appointments", {
    params: {
      upcomingAppointments: true,
      appointmentTypes: [APPOINTMENT_TYPE.DIETITIAN],
    },
  });

  return parseResponseData(response, z.array(appointmentSchema));
};

export const markCareValidateAsScheduled = async (
  subscriptionId: Subscription["id"],
) => {
  return await privateAPI.post(
    `patients/subscriptions/${subscriptionId}/intake-completed`,
  );
};
